import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { FC, useMemo } from 'react'
import { FaCrown, FaRocket } from 'react-icons/fa'
import { MdCheck } from 'react-icons/md'
import { PlanCode } from '../types/subscription'

export interface PaywallModalProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade?: () => void
  planCode?: PlanCode | null
}

const LEGACY_PLANS: ReadonlySet<PlanCode> = new Set([PlanCode.STANDARD, PlanCode.UNLIMITED])

const getPaywallContent = (planCode?: PlanCode | null) => {
  if (!planCode) {
    return {
      icon: FaRocket,
      title: 'Start Your Journey',
      description: 'Subscribe to a plan to unlock the full potential of RxPad.',
      buttonLabel: 'View Plans',
      hideButton: false,
      features: [
        '30 scripts per month',
        'Unlimited free admin users',
        'Send script via SMS and email',
        'Real Time Prescription Monitoring (RTPM)',
      ],
    }
  }

  if (planCode === PlanCode.UNLIMITED_ONE_YEAR) {
    return {
      icon: FaCrown,
      title: 'Need More Features?',
      description:
        'Your current plan does not include this feature. Please contact our support team to discuss upgrade options.',
      buttonLabel: '',
      hideButton: true,
      features: [
        'Unlimited tokens per month',
        'Create multiple scripts',
        'Share organization patients',
        'Priority Customer Support',
      ],
    }
  }

  if (LEGACY_PLANS.has(planCode)) {
    return {
      icon: FaCrown,
      title: 'Legacy Plan Update',
      description:
        'Your current plan is retiring. Upgrade now to access new features and improved performance.',
      buttonLabel: 'Update Plan',
      hideButton: false,
      features: [
        'Unlimited tokens per month',
        'Create multiple scripts',
        'Share organization patients',
        'Self-managed billing for multiple users',
      ],
    }
  }

  return {
    icon: FaCrown,
    title: 'Unlock Premium Features',
    description: 'Upgrade your plan to enable multiple scripts per prescription and more.',
    buttonLabel: 'View Plans',
    hideButton: false,
    features: [
      'Unlimited tokens per month',
      'Create multiple scripts',
      'Share organization patients',
      'Renew scripts (Coming soon)',
    ],
  }
}

export const PaywallModal: FC<PaywallModalProps> = ({ isOpen, onClose, onUpgrade, planCode }) => {
  const { icon, title, description, buttonLabel, hideButton, features } = useMemo(
    () => getPaywallContent(planCode),
    [planCode]
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isCentered
      motionPreset="slideInBottom"
      closeOnOverlayClick={false}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalCloseButton zIndex={2} />

        <ModalHeader pt={8} pb={2} textAlign="center">
          <VStack spacing={4}>
            <Flex
              p={4}
              borderRadius="full"
              bg="blue.50"
              color="blue.600"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={icon} boxSize={8} />
            </Flex>
            <VStack spacing={1}>
              <Heading size="lg" color="gray.900">
                {title}
              </Heading>
              <Text fontSize="md" fontWeight="normal" color="gray.600" maxW="sm" mx="auto">
                {description}
              </Text>
            </VStack>
          </VStack>
        </ModalHeader>

        <ModalBody py={6}>
          <Box bg="gray.50" p={6} borderRadius="xl" border="1px solid" borderColor="gray.100">
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
              color="gray.500"
              mb={4}
            >
              What's included
            </Text>
            <List spacing={3}>
              {features.map((feature) => (
                <ListItem key={feature} display="flex" alignItems="center">
                  <Flex
                    bg="blue.50"
                    borderRadius="full"
                    p="1"
                    mr={3}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={MdCheck} boxSize="14px" color="blue.600" />
                  </Flex>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    {feature}
                  </Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </ModalBody>

        <ModalFooter pb={8} pt={2} justifyContent="center">
          {hideButton ? (
            <Button
              as="a"
              w="full"
              size="lg"
              variant="primary"
              href="mailto:support@rxpad.com.au"
              borderRadius="xl"
              aria-label="Contact support"
            >
              Contact Us
            </Button>
          ) : (
            <Button
              w="full"
              size="lg"
              variant="primary"
              onClick={onUpgrade}
              isDisabled={!onUpgrade}
              borderRadius="xl"
            >
              {buttonLabel}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PaywallModal
